import React from "react";
import { SkeletonBox, SkeletonText, SkeletonCard, SkeletonButton } from "./SkeletonBox";

/**
 * HomePageSkeleton - Non-animated skeleton for TV Home page
 * 
 * Matches layout of:
 * - topSlider.jsx (Hero: 80vh, 80vw)
 * - moviesList.jsx (Cards: 250x350, rows with 60px left padding)
 * - content.jsx (Scrollable rows)
 */

function skeletonElement(className) {
  return function SkeletonElement(props) {
    var mergedClassName = className + (props.className ? " " + props.className : "");
    return <div {...props} className={mergedClassName} />;
  };
}

var HeroSkeleton = skeletonElement("legacy-skeleton-home-hero");
var HeroContent = skeletonElement("legacy-skeleton-home-hero-content");
var ContentRowWrapper = skeletonElement("legacy-skeleton-row");
var RowTitle = skeletonElement("legacy-skeleton-row-title");
var CardsContainer = skeletonElement("legacy-skeleton-cards");
var CardWrapper = skeletonElement("legacy-skeleton-card-wrap");
var MetadataRow = skeletonElement("legacy-skeleton-inline-row legacy-skeleton-metadata-row");
var MetadataItem = skeletonElement("legacy-skeleton-inline-item legacy-skeleton-metadata-item");
var ButtonsRow = skeletonElement("legacy-skeleton-inline-row legacy-skeleton-buttons-row");
var ButtonWrapper = skeletonElement("legacy-skeleton-inline-item legacy-skeleton-button-wrap");

/**
 * Hero Section Skeleton
 */
function HeroSectionSkeleton() {
  return (
    <HeroSkeleton>
      <HeroContent>
        {/* Title */}
        <SkeletonText width={400} size="xl" style={{ marginBottom: 16 }} />

        {/* English title */}
        <MetadataRow>
          <MetadataItem>
            <SkeletonText width={250} size="lg" />
          </MetadataItem>
        </MetadataRow>

        {/* Metadata: Year | Duration | Rating */}
        <MetadataRow>
          <MetadataItem>
            <SkeletonText width={80} size="md" />
          </MetadataItem>
          <MetadataItem>
            <SkeletonBox width={2} height={20} borderRadius={1} />
          </MetadataItem>
          <MetadataItem>
            <SkeletonText width={100} size="md" />
          </MetadataItem>
        </MetadataRow>

        {/* Watch Button */}
        <ButtonsRow>
          <ButtonWrapper>
            <SkeletonButton variant="primary" />
          </ButtonWrapper>
        </ButtonsRow>
      </HeroContent>
    </HeroSkeleton>
  );
}

// ============================================================================
// TURKYLAR ROW SKELETON
// Matches turkylar.jsx: 306x170 landscape cards, semi-transparent bg, -7rem translate
// ============================================================================

var TurkylarWrapper = skeletonElement("legacy-skeleton-turkylar");
var TurkylarTitle = skeletonElement("legacy-skeleton-turkylar-title");
var TurkylarCardsContainer = skeletonElement("legacy-skeleton-turkylar-cards");
var TurkylarCardWrapper = skeletonElement("legacy-skeleton-turkylar-card-wrap");

/**
 * Turkylar Row Skeleton
 * Matches turkylar.jsx: landscape cards 306x170 with semi-transparent background
 */
function TurkylarRowSkeleton({ cardCount = 5 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <TurkylarCardWrapper key={"tlr_" + i}>
        <SkeletonBox width={306} height={170} borderRadius={10} />
      </TurkylarCardWrapper>
    );
  }

  return (
    <TurkylarWrapper>
      <TurkylarTitle />
      <TurkylarCardsContainer>
        {cards}
      </TurkylarCardsContainer>
    </TurkylarWrapper>
  );
}

/**
 * Content Row Skeleton
 */
function ContentRowSkeleton({ cardType = "portrait", cardCount = 6 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <CardWrapper key={"card_" + i}>
        <SkeletonCard type={cardType} />
      </CardWrapper>
    );
  }

  return (
    <ContentRowWrapper>
      <RowTitle />
      <CardsContainer>
        {cards}
      </CardsContainer>
    </ContentRowWrapper>
  );
}

/**
 * HomePageSkeleton - Main export
 * Default: Hero + Turkylar + 1 MovieSlider row (matching real home page structure)
 */
function HomePageSkeleton({ showHero = true, rowCount = 1 }) {
  var rows = [];
  for (var i = 0; i < rowCount; i++) {
    rows.push(
      <ContentRowSkeleton
        key={"row_" + i}
        cardType="portrait"
        cardCount={6}
      />
    );
  }

  return (
    <div>
      {showHero && <HeroSectionSkeleton />}
      {showHero && <TurkylarRowSkeleton />}
      {rows}
    </div>
  );
}

export { HeroSectionSkeleton, ContentRowSkeleton, TurkylarRowSkeleton };
export default HomePageSkeleton;
